import {
    RawActivityEvent,
    RawRunEvent,
    RestrictedConnection,
    RestrictedPipeMovement,
    RestrictedRateOfPenetration
} from '../../utils/type'
import { STATIC_DATA_STATES } from '../chart-components'
import {
    emptyRestrictedConnectionErrors,
    emptyRestrictedPipeErrors,
    emptyRestrictedRateOfPenetrationErrors,
    emptyActivityErrors,
    ActivityErrors,
    RestrictedConnectionErrors,
    RestrictedPipeErrors,
    RestrictedRateOfPenetrationErrors
} from './types'

export function validateActivityFields(activityEvent: RawActivityEvent, runEvent: RawRunEvent) {
    const { tubularsIntervalPerTime: isTubularPerTime } = runEvent

    const { type: runType } = runEvent

    let pass = true
    const activityErrors: ActivityErrors = { ...emptyActivityErrors }

    const isMaterialTypeRequired = ['CASING_RUNNING', 'TUBING_HANDLING'].includes(activityEvent.tubularType)
    if (
        isMaterialTypeRequired &&
        (!activityEvent.materialType?.length || activityEvent.materialType === 'NOT_APPLICABLE')
    ) {
        pass = false
        activityErrors.materialType = true
    }

    if (!activityEvent.tubularType?.length) {
        pass = false
        activityErrors.tubularType = true
    }

    const isConnectionTypeRequired = ['CASING_RUNNING', 'TUBING_HANDLING'].includes(activityEvent.tubularType)
    if (
        isConnectionTypeRequired &&
        (!activityEvent.connectionType?.length || activityEvent.connectionType === 'NOT_APPLICABLE')
    ) {
        pass = false
        activityErrors.connectionType = true
    }

    if (
        (activityEvent.tubularType === 'CASING_RUNNING' || activityEvent.tubularType === 'DPR_RUNNING') &&
        (isNaN(activityEvent?.oD?.value ?? NaN) || (activityEvent.oD?.value ?? 0) < 0)
    ) {
        pass = false
        activityErrors.oD = true
    }

    if (isTubularPerTime) {
        // start
        if (!activityEvent.start) {
            pass = false
            activityErrors.start = true
        }
        // end
        if (!activityEvent.end && activityEvent.state !== STATIC_DATA_STATES.EMPTY) {
            pass = false
            activityErrors.end = true
        }
    } else {
        // startMD
        if (
            isNaN(activityEvent.startMD?.value ?? NaN) ||
            (activityEvent.startMD?.value ?? 0) < 0 ||
            !activityEvent.startMD?.uom?.name?.length
        ) {
            pass = false
            activityErrors.startMD = true
        }

        // finalMD
        if (
            isNaN(activityEvent.finalMD?.value ?? NaN) ||
            (runType !== 'RISER' && (activityEvent.finalMD?.value ?? 0) < (activityEvent.startMD?.value ?? 0)) ||
            !activityEvent.finalMD?.uom?.name?.length
        ) {
            pass = false
            activityErrors.finalMD = true
        }
    }
    return { pass, errors: activityErrors }
}

export function validateRestrictedPipeFields(
    restrictedPipe: RestrictedPipeMovement,
    activityEvent: RawActivityEvent,
    runEvent: RawRunEvent
): {
    pass: boolean
    errors: RestrictedPipeErrors
} {
    const isRestricted = !['UNRESTRICTED'].includes(restrictedPipe?.restrictedPipeMovementType)
    const { tubularsIntervalPerTime: isTubularPerTime } = runEvent

    let pass = true
    const restrictedPipeErrors: RestrictedPipeErrors = { ...emptyRestrictedPipeErrors }

    // restricted pipe movement type
    if (!restrictedPipe.restrictedPipeMovementType?.length) {
        pass = false
        restrictedPipeErrors.restrictedPipeMovementType = true
    }

    if (isRestricted) {
        // restricted pipe movement speed
        if (
            isNaN(restrictedPipe.restrictedPipeMovementSpeed?.value ?? NaN) ||
            (restrictedPipe.restrictedPipeMovementSpeed?.value ?? 0) < 0 ||
            (!restrictedPipe.restrictedPipeMovementSpeed?.uom?.name?.length &&
                !restrictedPipe.restrictedPipeMovementSpeed?.name?.length)
        ) {
            pass = false
            restrictedPipeErrors.restrictedPipeMovementSpeed = true
        }

        if (isTubularPerTime) {
            // start
            if (!restrictedPipe.start) {
                pass = false
                restrictedPipeErrors.start = true
            }

            // end
            if (!restrictedPipe.end) {
                pass = false
                restrictedPipeErrors.end = true
            }
        } else {
            // startMD
            if (
                isNaN(restrictedPipe.startMD?.value ?? NaN) ||
                (restrictedPipe.startMD?.value ?? 0) < 0 ||
                !restrictedPipe.startMD?.uom?.name?.length
            ) {
                pass = false
                restrictedPipeErrors.startMD = true
            }

            // finalMD
            if (
                isNaN(restrictedPipe.finalMD?.value ?? NaN) ||
                (restrictedPipe.finalMD?.value ?? 0) < (restrictedPipe.startMD?.value ?? 0) ||
                !restrictedPipe.finalMD?.uom?.name?.length
            ) {
                pass = false
                restrictedPipeErrors.finalMD = true
            }
        }
    }
    return { pass, errors: restrictedPipeErrors }
}
export function validateRestrictedConnectionFields(
    restrictedConnection: RestrictedConnection,
    activityEvent: RawActivityEvent,
    runEvent: RawRunEvent
) {
    const isRestricted = !['UNRESTRICTED'].includes(restrictedConnection?.restrictionType)
    const { tubularsIntervalPerTime: isTubularPerTime } = runEvent

    let pass = true
    const restrictedConnectionErrors: RestrictedConnectionErrors = { ...emptyRestrictedConnectionErrors }

    // restricted connection type
    if (!restrictedConnection.restrictionType?.length) {
        pass = false
        restrictedConnectionErrors.restrictionType = true
    }

    if (isRestricted) {
        // restricted time connection
        if (
            isNaN(restrictedConnection.restrictedTimeConnection?.value ?? NaN) ||
            (restrictedConnection.restrictedTimeConnection?.value ?? 0) < 0 ||
            (!restrictedConnection.restrictedTimeConnection?.uom?.name?.length &&
                !restrictedConnection.restrictedTimeConnection?.name?.length)
        ) {
            pass = false
            restrictedConnectionErrors.restrictedTimeConnection = true
        }

        if (isTubularPerTime) {
            // start
            if (!restrictedConnection.start) {
                pass = false
                restrictedConnectionErrors.start = true
            }

            // end
            if (!restrictedConnection.end) {
                pass = false
                restrictedConnectionErrors.end = true
            }
        } else {
            // startMD
            if (
                isNaN(restrictedConnection.startMD?.value ?? NaN) ||
                (restrictedConnection.startMD?.value ?? 0) < 0 ||
                !restrictedConnection.startMD?.uom?.name?.length
            ) {
                pass = false
                restrictedConnectionErrors.startMD = true
            }

            // finalMD
            if (
                isNaN(restrictedConnection.finalMD?.value ?? NaN) ||
                (restrictedConnection.finalMD?.value ?? 0) < (restrictedConnection.startMD?.value ?? 0) ||
                !restrictedConnection.finalMD?.uom?.name?.length
            ) {
                pass = false
                restrictedConnectionErrors.finalMD = true
            }
        }
    }
    return { pass, errors: restrictedConnectionErrors }
}
export function validateRestrictedRateOfPenetrationFields(
    restrictedRateOfPenetration: RestrictedRateOfPenetration,
    activityEvent: RawActivityEvent,
    runEvent: RawRunEvent
) {
    const isRestricted = !['UNRESTRICTED'].includes(restrictedRateOfPenetration?.restrictedRopType)
    const { tubularsIntervalPerTime: isTubularPerTime } = runEvent

    let pass = true
    const restrictedRateOfPenetrationErrors: RestrictedRateOfPenetrationErrors = {
        ...emptyRestrictedRateOfPenetrationErrors
    }

    // restricted ROP
    if (!restrictedRateOfPenetration.restrictedRopType?.length) {
        pass = false
        restrictedRateOfPenetrationErrors.restrictedRopType = true
    }

    if (isRestricted) {
        // restricted ROP speed
        if (
            isNaN(restrictedRateOfPenetration.restrictedRopSpeed?.value ?? NaN) ||
            (restrictedRateOfPenetration.restrictedRopSpeed?.value ?? 0) < 0 ||
            (!restrictedRateOfPenetration.restrictedRopSpeed?.uom?.name?.length &&
                !restrictedRateOfPenetration.restrictedRopSpeed?.name?.length)
        ) {
            pass = false
            restrictedRateOfPenetrationErrors.restrictedRopSpeed = true
        }

        if (isTubularPerTime) {
            // start
            if (isTubularPerTime && !restrictedRateOfPenetration.start) {
                pass = false
                restrictedRateOfPenetrationErrors.start = true
            }

            // end
            if (isTubularPerTime && !restrictedRateOfPenetration.end) {
                pass = false
                restrictedRateOfPenetrationErrors.end = true
            }
        } else {
            // startMD
            if (
                isNaN(restrictedRateOfPenetration.startMD?.value ?? NaN) ||
                (restrictedRateOfPenetration.startMD?.value ?? 0) < 0 ||
                !restrictedRateOfPenetration.startMD?.uom?.name?.length
            ) {
                pass = false
                restrictedRateOfPenetrationErrors.startMD = true
            }

            // finalMD
            if (
                isNaN(restrictedRateOfPenetration.finalMD?.value ?? NaN) ||
                (restrictedRateOfPenetration.finalMD?.value ?? 0) < (restrictedRateOfPenetration.startMD?.value ?? 0) ||
                !restrictedRateOfPenetration.finalMD?.uom?.name?.length
            ) {
                pass = false
                restrictedRateOfPenetrationErrors.finalMD = true
            }
        }
    }
    return { pass, errors: restrictedRateOfPenetrationErrors }
}
